import { HeatMapOutlined } from '@ant-design/icons'
import { Space, Table, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { StoreState } from 'src/store/configureStore'
import useTranslate from 'src/utils/useTranslate'
import { useValue } from 'src/utils/useValue'
import { geekblue } from "@ant-design/colors"

const { Text } = Typography


export const PredictedProperties = ({ experiments, title }: any) => {
    const [t] = useTranslate()
    const { getValue } = useValue()
    const displayNames = useSelector((state: StoreState) => state.displayNames.data?.properties)
    const [expandedRows, setExpandedRows] = useState<any[]>([]);

    const columns: any = useMemo(() => [
        {
            key: "category",
            dataIndex: "category",
            title: t("common.category"),
            fixed: "left",
            width: 150,
            align: "center" as any,
        },
        {
            key: "predicted_properties",
            dataIndex: "predicted_properties",
            title: t("common.property"),
            fixed: "left",
            width: 200,
            align: "center" as any,
            render: (text: any, record: any) => {
                return record?.property_name || displayNames?.[text]?.name || text
            }
        },
        {
            key: "unit",
            dataIndex: "unit",
            title: t("common.unit"),
            fixed: "left",
            width: 100,
            align: "center" as any,
        },
        ...experiments.map((res: any) => ({
            key: res?.id_set?.formulation_id,
            dataIndex: res?.id_set?.formulation_id,
            title: res?.meta?.display_id,
            width: 150,
            align: "center" as any,
        }))
    ], [experiments, displayNames, t])

    const data = useMemo(() => {
        const predictedProperties = [...new Set(experiments.flatMap((res: any) => Object.keys(res?.predicted_properties || {})))].map((res: any) => ({ predicted_properties: res }))
        experiments.forEach((exp: any) => {
            predictedProperties.forEach((record: any, index: number) => {
                record.unit = exp?.predicted_properties?.[record?.predicted_properties]?.unit
                record.category = exp?.predicted_properties?.[record?.predicted_properties]?.category
                record["property_name"] = exp?.predicted_properties?.[record?.predicted_properties]?.name
                const value = exp?.predicted_properties?.[record?.predicted_properties]?.value
                record[exp?.id_set?.formulation_id] = getValue(value) ?? "-"
                record.propertyData = exp?.predicted_properties?.[record?.predicted_properties]?.property_data
                record.key = index
            })
        })
        return predictedProperties
    }, [experiments, getValue])


    return (
        <Space direction="vertical" size={"large"} style={{ width: "100%" }}>
            <Table
                title={() => !!title ? <Space><HeatMapOutlined /><Text strong type="secondary">{title}</Text> </Space> : null}
                className={"report-table"}
                size='small'
                scroll={{ x: 450 }}
                bordered
                columns={columns}
                dataSource={data}
                pagination={false}
                expandable={{
                    expandedRowRender: (record: any) => {
                        const columns = [
                            {
                                dataIndex: "models",
                                key: "models",
                                width: "470px",
                                title: (
                                    <Text strong style={{ paddingLeft: "26px" }}>
                                        {t("common.models")}
                                    </Text>
                                ),
                                align: "center" as "center",
                                fixed: "center" as "center",
                                render: (text: any, record: any) => {
                                    if (record?.models === "Polymerize") {
                                        return {
                                            props: {
                                                style: { background: geekblue[0] },
                                                className: 'checkbox-spacing'
                                            },
                                            children: <Text>{text}</Text>,
                                        }
                                    }
                                    return {
                                        props: {
                                            style: {},
                                            className: 'checkbox-spacing'
                                        },
                                        children: <Text>{text}</Text>,
                                    }
                                },
                            },
                            {
                                dataIndex: "unit",
                                key: "unit",
                                width: "80px",
                                title: (
                                    <Text strong>
                                        {t("common.unit")}
                                    </Text>
                                ),
                                align: "center" as "center",
                                fixed: "center" as "center",
                                render: (text: any, record: any) => {
                                    if (record?.models === "Polymerize") {
                                        return {
                                            props: {
                                                style: { background: geekblue[0] },
                                                className: 'checkbox-spacing'
                                            },
                                            children: <Text>{text}</Text>,
                                        }
                                    }
                                    return {
                                        props: {
                                            style: {},
                                            className: 'checkbox-spacing'
                                        },
                                        children: <Text>{text}</Text>,
                                    }
                                },
                            },
                            ...experiments.map((res: any) => ({
                                title: () => (
                                    <Space
                                        style={{
                                            alignItems: "center",
                                            gap: "10px",
                                            wordBreak: "break-word",
                                            lineHeight: 1,
                                            paddingLeft: "26px"
                                        }}
                                    >
                                        <Text strong>{res?.meta?.display_id}</Text>
                                    </Space>
                                ),
                                align: "center" as "center",
                                fixed: "center" as "center",
                                dataIndex: res?.id_set?.formulation_id,
                                width: "170px",
                                key: res?.id_set?.formulation_id,
                                render: (text: any, record: any) => {
                                    if (record?.models === "Polymerize") {
                                        return {
                                            props: {
                                                style: { background: geekblue[0] },
                                                className: 'checkbox-spacing'
                                            },
                                            children: <Text>{text}</Text>,
                                        }
                                    }
                                    return {
                                        props: {
                                            style: {},
                                            className: 'checkbox-spacing'
                                        },
                                        children: <Text>{text}</Text>,
                                    }
                                },
                            })),
                        ]
                        const recordData = [{ models: "Polymerize" }, ...(Object.keys(record?.propertyData || {}).map((res: any) => ({ models: res })))]
                        experiments.forEach((exp: any) => {
                            recordData.forEach((res: any, i: number) => {
                                res.unit = exp?.predicted_properties?.[record?.predicted_properties]?.unit
                                const value = res?.models === "Polymerize" ? record?.[exp?.id_set?.formulation_id] : record?.propertyData?.[res?.models]?.value
                                res[exp?.id_set?.formulation_id] = getValue(value) ?? "-"
                                res.key = i
                            })
                        })
                        return (
                            <Table
                                columns={columns}
                                dataSource={recordData}
                                size="small"
                                pagination={false}
                                scroll={{ x: 450 }}
                                className="report-table"
                            />
                        );
                    },
                    expandedRowKeys: expandedRows,
                    rowExpandable: (record: any) => !!record?.propertyData,
                    onExpand: (expanded: boolean, record: any) => {
                        if (expanded) {
                            setExpandedRows([...expandedRows, record?.key])
                        } else if (!expanded) {
                            setExpandedRows([...expandedRows.filter(rowId => rowId !== record?.key)])
                        }
                    },
                }}
            />
        </Space>
    )
}
